import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  GroupIcon,
  Note2Icon,
  NoteIcon,
  SettingIcon,
  ToolIcon,
  AddIcon,
  PaymentIcon,
  AgencyIcon
} from 'src/assets/icons';
import { RoutePath } from 'src/enums/routePath';
import { unSlash } from 'src/utils/route';
import { Menu, MenuProps, styled } from 'core-ui-2';
import { Button } from 'core-ui';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isArray } from 'lodash';
import { ProfileContext } from 'src/contexts/profile';
import { PermissionList } from 'src/enums/permission';
import {
  getListDontHighLightMenu,
  removeSearchPage,
  setListDontHighLightMenu
} from 'src/utils/common';
import { DASHBOARD_PERMISSIONS } from 'src/pages/admin/Config/utils';
import { useAgencyCount } from 'src/contexts/agencyCount';
import QRIcon from 'src/assets/icons/menu/QRIcon';
import SaleIcon from 'src/assets/icons/menu/SaleIcon';
import DashboardIcon from 'src/assets/icons/menu/DashboardIcon';
import MasterIcon from 'src/assets/icons/menu/MasterIcon';
import useResponsive from 'src/hooks/useResponsive';
import { FEATURE_FLAGS } from 'src/constants/featureFlags';

const StyledMenu = styled(Menu)`
  border-inline-end: none !important;

  .ant-menu-submenu {
    .ant-menu-submenu-title {
      margin-inline: 0px;
      margin-block: 0px;
      width: 100%;
      border-radius: 0px;
      color: #8c8c8c;
      font-weight: 600;
    }
  }

  .ant-menu-item {
    margin-inline: 0px;
    margin-block: 0px;
    width: 100%;
    border-radius: 0px;
    color: #8c8c8c;
    font-weight: 600;

    &.ant-menu-item-selected {
      position: relative;
      background-color: rgba(82, 114, 189, 0.15);
      color: #5272bd;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        border-left: 3px solid #5272bd !important;
      }
    }
  }
`;

interface SideMenuProps {
  onItemClick?: () => void;
}

const SideMenu: React.FC<SideMenuProps> = ({ onItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, hasAnyOfRoles, agency } = useContext(ProfileContext);
  const { unapprovedCount, fetchUnapprovedCount } = useAgencyCount();
  const { isMobile } = useResponsive();

  useMemo(() => {
    removeSearchPage();
    setListDontHighLightMenu('', true, true);
  }, []);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  useEffect(() => {
    fetchUnapprovedCount();
  }, []);

  const menuItems: MenuProps['items'] = [
    // {
    //   key: 'createSubmitAndImei',
    //   label: '新規作成',
    //   icon: React.createElement(AddIcon),
    //   style: {
    //     display:
    //       hasRole(PermissionList.SUBMIT_AGREEMENT_CREATE) ||
    //       hasRole(PermissionList.IMEI_CREATE)
    //         ? 'block'
    //         : 'none'
    //   },
    //   children: [
    //     {
    //       key:
    //         unSlash(RoutePath.ADMIN) +
    //         RoutePath.INSURANCE_APPLICATION +
    //         RoutePath.CREATE,
    //       label: (
    //         <Link
    //           to={
    //             RoutePath.ADMIN +
    //             RoutePath.INSURANCE_APPLICATION +
    //             RoutePath.CREATE
    //           }
    //         >
    //           <Button
    //             type="primary"
    //             className="rounded-lg font-bold border-custom-blue4 h-8 !w-36"
    //           >
    //             同意書新規作成
    //           </Button>
    //         </Link>
    //       ),
    //       style: {
    //         display: hasRole(PermissionList.SUBMIT_AGREEMENT_CREATE)
    //           ? 'flex'
    //           : 'none'
    //       }
    //     },
    //     {
    //       key: unSlash(RoutePath.ADMIN) + RoutePath.IMEI + RoutePath.CREATE,
    //       label: (
    //         <Link to={RoutePath.ADMIN + RoutePath.IMEI + RoutePath.CREATE}>
    //           <Button
    //             type="primary"
    //             className="rounded-lg font-bold border-custom-blue4 h-8 !w-36"
    //           >
    //             IMEI情報取得
    //           </Button>
    //         </Link>
    //       ),
    //       style: {
    //         display: hasRole(PermissionList.IMEI_CREATE) ? 'flex' : 'none'
    //       }
    //     }
    //   ]
    // },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.DASHBOARD,
      icon: React.createElement(DashboardIcon),
      label: (
        <Link to={RoutePath.ADMIN + RoutePath.DASHBOARD}>ダッシュボード</Link>
      ),
      style: {
        display:
          FEATURE_FLAGS.ENABLE_DASHBOARD && hasAnyOfRoles(DASHBOARD_PERMISSIONS)
            ? 'flex'
            : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.PAYMENTS,
      icon: <PaymentIcon isMobile={isMobile} />,
      label: <Link to={RoutePath.ADMIN + RoutePath.PAYMENTS}>決済管理</Link>,
      style: {
        display: hasRole(PermissionList.PAYMENT_MANAGER_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.SALES,
      icon: <SaleIcon isMobile={isMobile} />,
      label: <Link to={RoutePath.ADMIN + RoutePath.SALES}>売上管理</Link>,
      style: {
        display: hasRole(PermissionList.SALES_MANAGER_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.AGENCY,
      icon: React.createElement(AgencyIcon),
      label: <Link to={RoutePath.ADMIN + RoutePath.AGENCY}>代理店</Link>,
      style: {
        display:
          hasRole(PermissionList.AGENCY_LIST) && !agency?.agencyId
            ? 'flex'
            : 'none'
      }
    },
    {
      key: 'customers',
      icon: React.createElement(GroupIcon),
      label: '顧客管理',
      style: {
        display: hasAnyOfRoles([
          PermissionList.CUSTOMER_LIST,
          PermissionList.CLIENT_LIST
        ])
          ? ''
          : 'none'
      },
      children: [
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.CLIENTS,
          label: <Link to={RoutePath.ADMIN + RoutePath.CLIENTS}>会員情報</Link>,
          style: {
            display: hasRole(PermissionList.CLIENT_LIST) ? 'flex' : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.CUSTOMERS,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.CUSTOMERS}>契約情報</Link>
          ),
          style: {
            display: hasRole(PermissionList.CUSTOMER_LIST) ? 'flex' : 'none'
          }
        }
      ]
    },
    {
      key: unSlash(RoutePath.REPAIRS),
      icon: React.createElement(ToolIcon),
      label: <Link to={RoutePath.REPAIRS}>修理</Link>,
      style: {
        display: hasRole(PermissionList.REPAIR_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.INSURANCE_APPLICATION,
      icon: React.createElement(Note2Icon),
      label: (
        <Link to={RoutePath.ADMIN + RoutePath.INSURANCE_APPLICATION}>
          同意書管理
        </Link>
      ),
      style: {
        display: hasRole(PermissionList.SUBMIT_AGREEMENT_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.ACCOUNT,
      icon: React.createElement(NoteIcon),
      label: <Link to={RoutePath.ADMIN + RoutePath.ACCOUNT}>アカウント</Link>,
      style: {
        display: hasRole(PermissionList.ACCOUNT_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: unSlash(RoutePath.ADMIN) + RoutePath.QR_CODE,
      icon: React.createElement(QRIcon),
      label: (
        <Link to={RoutePath.ADMIN + RoutePath.QR_CODE}>申込用QRコード管理</Link>
      ),
      style: {
        display: hasRole(PermissionList.QR_REGISTER_LIST) ? 'flex' : 'none'
      }
    },
    {
      key: 'settings',
      icon: React.createElement(SettingIcon),
      label: '設定',
      style: {
        display: hasAnyOfRoles([
          PermissionList.PERMISSION_LIST,
          PermissionList.AGENCY_AUTHORITY_LIST,
          PermissionList.ENABLING_REPAIR_AGREEMENT_LINK,
          PermissionList.COUNT_CALLS_LIST,
          PermissionList.COUNT_REPAIRS_LIST
        ])
          ? ''
          : 'none'
      },
      children: [
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.CONFIG,
          label: <Link to={RoutePath.ADMIN + RoutePath.CONFIG}>権限設定</Link>,
          style: {
            display: hasRole(PermissionList.PERMISSION_LIST) ? 'flex' : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.AGENCY_AUTHORITY,
          label: (
            <div className="flex items-center justify-between">
              <Link to={RoutePath.ADMIN + RoutePath.AGENCY_AUTHORITY}>
                代理店権限設定
              </Link>
              {unapprovedCount >= 0 && (
                <div className="ml-2 bg-red-500 text-white rounded-full w-min px-2 flex items-center justify-center text-xs border-white border">
                  {unapprovedCount > 999 ? '999+' : unapprovedCount}
                </div>
              )}
            </div>
          ),
          style: {
            display:
              hasRole(PermissionList.AGENCY_AUTHORITY_LIST) && !agency?.agencyId // Agency accounts are not allowed to enter this screen.
                ? 'flex'
                : 'none'
          }
        },
        {
          key:
            unSlash(RoutePath.ADMIN) + RoutePath.CONFIG + RoutePath.EXPIRATION,
          label: (
            <Link
              to={RoutePath.ADMIN + RoutePath.CONFIG + RoutePath.EXPIRATION}
            >
              閲覧期限設定
            </Link>
          ),
          style: {
            display: hasRole(PermissionList.ENABLING_REPAIR_AGREEMENT_LINK)
              ? 'flex'
              : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.COUNT_SETTING_NUMBER,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.COUNT_SETTING_NUMBER}>
              件数設定
            </Link>
          ),
          style: {
            display:
              FEATURE_FLAGS.ENABLE_COUNT_SETTING_NUMBER &&
              (hasRole(PermissionList.COUNT_CALLS_LIST) ||
                hasRole(PermissionList.COUNT_REPAIRS_LIST))
                ? 'flex'
                : 'none'
          }
        }
      ]
    },
    {
      key: 'master',
      icon: React.createElement(MasterIcon),
      label: 'マスター',
      style: {
        display: hasAnyOfRoles([
          PermissionList.MAKER_LIST,
          PermissionList.MODEL_LIST,
          PermissionList.CATEGORY_LIST,
          PermissionList.PLAN_LIST
        ])
          ? ''
          : 'none'
      },
      children: [
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.MAKERS,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.MAKERS}>
              メーカー名マスター
            </Link>
          ),
          style: {
            display: hasRole(PermissionList.MAKER_LIST) ? 'flex' : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.MODELS,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.MODELS}>型番マスター</Link>
          ),
          style: {
            display: hasRole(PermissionList.MODEL_LIST) ? 'flex' : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.PRODUCTS,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.PRODUCTS}>
              製品カテゴリーマスター
            </Link>
          ),
          style: {
            display: hasRole(PermissionList.CATEGORY_LIST) ? 'flex' : 'none'
          }
        },
        {
          key: unSlash(RoutePath.ADMIN) + RoutePath.PLANS,
          label: (
            <Link to={RoutePath.ADMIN + RoutePath.PLANS}>プランマスター</Link>
          ),
          style: {
            display: hasRole(PermissionList.PLAN_LIST) ? 'flex' : 'none'
          }
        }
      ]
    }
  ];

  const collectKey = (
    menuItems: MenuProps['items'],
    result: { [key: string]: string }
  ) => {
    menuItems?.forEach((item) => {
      const key = item?.key?.toString() || '';
      result[key] = key;
      const parseItem = item as any;
      if (isArray(parseItem?.children)) {
        collectKey(parseItem?.children, result);
      }
    });
    return result;
  };

  const patternKeys = useMemo(() => {
    const result: { [key: string]: string } = {};
    collectKey(menuItems, result);
    return result;
  }, [menuItems]);

  const arrayPathname = location.pathname.split('/');
  let key = '';

  const listDontHighLight = getListDontHighLightMenu();
  arrayPathname.reverse().forEach((_, index) => {
    const keyPath = [...arrayPathname].splice(index, arrayPathname.length);
    const keyString = keyPath
      .reverse()
      .join('/')
      .replace('/', '') as keyof typeof patternKeys;
    if (
      listDontHighLight[keyString] &&
      keyString != location.pathname.replace('/', '')
    )
      return '';

    if (patternKeys[keyString] && !key) key = patternKeys[keyString];
  });

  useEffect(() => {
    // After creating/editing the information, the screen returns to the middle of the list page
    const containerScroll = document.querySelector(
      '#root > div > div > div > div > .ant-layout-content'
    ) as HTMLDivElement;
    const containerScrollOutSide = document.querySelector(
      '#root > div > div > div > div'
    ) as HTMLDivElement;
    if (containerScroll) containerScroll.scrollTop = 0;
    if (containerScrollOutSide) containerScrollOutSide.scrollTop = 0;

    // When the page is reloaded, the keys selected in the parent item will be expanded
    const path = location.pathname;

    const keyMapping: Record<string, string[]> = {
      // It only hides, it doesn't delete.
      // createSubmitAndImei: [
      //   '/admin/submit-agreement/create',
      //   '/admin/imei/create'
      // ],
      customers: ['/admin/customers', '/admin/clients'],
      settings: [
        '/admin/config',
        '/admin/agency-authority',
        '/admin/config/expiration',
        '/admin/count-setting-number'
      ],
      master: [
        '/admin/makers',
        '/admin/models',
        '/admin/products',
        '/admin/plans'
      ]
    };

    const matchedKeys = Object.entries(keyMapping)
      .filter(([_, paths]) => paths.some((p) => path.includes(p)))
      .map(([key]) => key);

    setOpenKeys((prevKeys) =>
      Array.from(new Set([...prevKeys, ...matchedKeys]))
    );
  }, [location.pathname]);

  const refPreKey = useRef(key);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    setListDontHighLightMenu(info.key, false);
    navigate(info.key);
    if (key !== refPreKey.current) {
      removeSearchPage();
      refPreKey.current = key;
    }
    onItemClick?.();
  };

  const goToSubmitRepairAgreementPage = () => {
    navigate(
      RoutePath.ADMIN + RoutePath.INSURANCE_APPLICATION + RoutePath.CREATE
    );
    setListDontHighLightMenu(
      unSlash(RoutePath.ADMIN) + RoutePath.INSURANCE_APPLICATION,
      true
    );
  };

  return (
    <aside className="h-full relative">
      <StyledMenu
        mode="inline"
        items={menuItems}
        onClick={handleMenuClick}
        selectedKeys={[key]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
      />
    </aside>
  );
};

export default SideMenu;
